import React, { useState, useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import GameVideoAnalytics from './GameVideoAnalytics';
import { useAuth } from '../contexts/AuthContext';

/**
 * Admin → Аналитика tab.
 *
 * Lists every game that has interactive video and lets the admin drill into
 * per-game playback statistics (heatmap, drop-off, interactions, etc.).
 * The heavy lifting is done by GameVideoAnalytics; we only handle game
 * discovery + selection here.
 */
export default function VideoAnalytics({ games = [], panoramas = [] }) {
  const { getAuthHeader } = useAuth();

  // Only games that actually have video are useful for analytics. Sort newest
  // first so admins can quickly find recent ones.
  const videoGames = useMemo(() => {
    return games
      .filter(g => g.has_video || g.has_interactive_video || g.video_hls_ready)
      .slice()
      .sort((a, b) => (b.published_at || b.created_at || '').localeCompare(a.published_at || a.created_at || ''));
  }, [games]);

  const [selectedId, setSelectedId] = useState(() => videoGames[0]?.id || '');

  // Keep selection valid if the games list changes (e.g., after refetch).
  React.useEffect(() => {
    if (!selectedId && videoGames.length > 0) setSelectedId(videoGames[0].id);
    if (selectedId && !videoGames.find(g => g.id === selectedId) && videoGames.length > 0) {
      setSelectedId(videoGames[0].id);
    }
  }, [videoGames, selectedId]);

  const selectedGame = videoGames.find(g => g.id === selectedId);
  const gamePanoramas = useMemo(
    () => panoramas.filter(p => p.game_id === selectedId),
    [panoramas, selectedId]
  );

  if (videoGames.length === 0) {
    return (
      <div data-testid="analytics-empty" style={{ padding: 24, color: '#6b7280' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <BarChart3 size={20} /> Аналитика видео
        </h3>
        <p>Нет игр с загруженным интерактивным видео.</p>
      </div>
    );
  }

  return (
    <div data-testid="analytics-tab" style={{ padding: '0 4px' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e5e7eb',
        }}
      >
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 16, fontWeight: 700 }}>
          <BarChart3 size={18} /> Аналитика видео
        </h3>
        <label style={{ fontSize: 13, color: '#6b7280', marginLeft: 'auto' }}>
          Игра:
        </label>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          data-testid="analytics-game-select"
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
            background: 'white', fontSize: 14, minWidth: 280, maxWidth: 520,
          }}
        >
          {videoGames.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>

      {selectedGame && (
        <GameVideoAnalytics
          key={selectedGame.id}
          gameId={selectedGame.id}
          getAuthHeader={getAuthHeader}
          videoDuration={selectedGame.video_duration_sec || 300}
          panoramas={gamePanoramas}
        />
      )}
    </div>
  );
}
